import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CalendarDays, CheckSquare, RefreshCw, CheckCheck, MessageSquare, Workflow, Receipt, AlertTriangle, Clock, FileText } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { pt } from "date-fns/locale";

const META = {
  EVENT_PUBLISHED: { icon: CalendarDays, color: "#A886CD" },
  TASK_ASSIGNED: { icon: CheckSquare, color: "#5B8DEF" },
  TASK_DUE: { icon: Clock, color: "#E5A94C" },
  EVENT_TODAY: { icon: CalendarDays, color: "#4C9A8E" },
  RENEWAL_DUE: { icon: RefreshCw, color: "#E5A94C" },
  MENTION: { icon: MessageSquare, color: "#A886CD" },
  AUTOMATION: { icon: Workflow, color: "#5B8DEF" },
  BILLING_DUE_SOON: { icon: Clock, color: "#E5A94C" },
  BILLING_OVERDUE: { icon: AlertTriangle, color: "#D64545" },
  BILLING_TO_INVOICE: { icon: FileText, color: "#A886CD" },
  BILLING_WEEKLY: { icon: Receipt, color: "#4C9A8E" },
};

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: notifs = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  const unread = notifs.filter((n) => !n.read).length;

  const openNotif = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
    if (n.link) navigate(n.link);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          data-testid="notif-bell"
          aria-label="Notificações"
        >
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-black text-[10px] font-poppins font-semibold flex items-center justify-center"
              data-testid="notif-count"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-poppins font-semibold text-sm">Notificações</span>
          {unread > 0 && (
            <button onClick={readAll} className="text-xs text-primary flex items-center gap-1 hover:underline" data-testid="notif-read-all">
              <CheckCheck className="h-3.5 w-3.5" /> Marcar todas como lidas
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifs.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">Sem notificações.</div>
          )}
          {notifs.map((n) => {
            const meta = META[n.type] || META.TASK_ASSIGNED;
            const Icon = meta.icon;
            return (
              <button
                key={n.id}
                onClick={() => openNotif(n)}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border/60 hover:bg-muted/50 transition-colors ${n.read ? "opacity-60" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}22` }}>
                  <Icon className="h-4 w-4" style={{ color: meta.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-poppins font-medium text-sm truncate">{n.title}</span>
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                  </div>
                  {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 font-roboto">{n.body}</p>}
                  <span className="text-[11px] text-muted-foreground">
                    {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: pt }) : ""}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
